import { Injectable } from '@angular/core';
import {
  Observable, BehaviorSubject, of,
  map, switchMap,
  withLatestFrom, first, filter,
  tap
} from 'rxjs';

import {
  ITempImprovementPatientInsurances,
  IBenefitsInformation
} from '../models/temp-improvement-patient-insurances.interface';
import { ClientInsuranceHttpService } from '../services/client-insurance.httpservice';

@Injectable({
  providedIn: 'root'
})
export class PatientInsuranceStore {
  private fullListSubject: BehaviorSubject<ITempImprovementPatientInsurances[]>
    = new BehaviorSubject<ITempImprovementPatientInsurances[]>([]);

  private suggestedPayerModalSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private benefitsInfoSubject = new BehaviorSubject<Map<number, IBenefitsInformation>>(new Map());
  private benefitsInfo$ = this.benefitsInfoSubject.asObservable();

  private get fullList$(): Observable<ITempImprovementPatientInsurances[]> {
    return this.fullListSubject.asObservable();
  }

  get showSuggestedPayerModal$(): Observable<boolean> {
    return this.suggestedPayerModalSubject.asObservable();
  }

  get fullInsuranceList$(): Observable<ITempImprovementPatientInsurances[]> {
    return this.fullList$;
  }
  /**
   * Return EAP insurance list.
   */
  get eapList$(): Observable<ITempImprovementPatientInsurances[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => i.providerNetworkCompany?.eap))
    );
  }

  /**
   * Return Medicare insurance list.
   */
  get medicareList$(): Observable<ITempImprovementPatientInsurances[]> {
    return this.patientInsuranceList$.pipe(
      map((ins) => ins.filter((i) => i.providerNetworkCompany?.medicare || i.providerNetworkCompany?.medicareAdvantage))
    );
  }

  /**
   * Return Veterans Affairs insurance list.
   */
  get veteransAffairsList$(): Observable<ITempImprovementPatientInsurances[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => i.providerNetworkCompany?.veteransAffairs))
    );
  }

  /**
   * Return whether the client has any cash pay insurance.
   */
  get hasCashPay$(): Observable<boolean> {
    return this.fullList$.pipe(
      map((ins) => ins.some((i) => i.providerNetworkCompany?.isCashPay))
    );
  }

  /**
   * Return true when none of the client's insurances require a credit card.
   * Use this to skip asking for a card when no insurance has requireCreditCard=true.
   */
  get noInsuranceRequiresCreditCard$(): Observable<boolean> {
    return this.fullList$.pipe(
      map((ins) => !ins.some((i) => i.providerNetworkCompany?.requireCreditCard === true))
    );
  }

  /**
   * Return non-EAP and non-self-pay insurance list.
   */
  get patientInsuranceList$(): Observable<ITempImprovementPatientInsurances[]> {
    return this.fullList$.pipe(
      map((ins) => ins.filter((i) => this.isRegularInsurance(i)))
    );
  }

  constructor(
    private clientInsuranceHttpService: ClientInsuranceHttpService
  ) {
    this.loadPatientInsuranceList();
  }

  /**
   * Load list of insurances for client account.
   */
  loadPatientInsuranceList(): void {
    this.clientInsuranceHttpService.listTempImprovement({ view: 'with_provider_network_detail' })
      .subscribe((resp) => this.fullListSubject.next(resp));
  }

  /**
   * Load list of insurances and return an observable.
   * Useful for chaining with other observables.
   */
  loadPatientInsuranceList$(): Observable<void> {
    return this.clientInsuranceHttpService.listTempImprovement({ view: 'with_provider_network_detail' }).pipe(
      map((resp) => {
        this.fullListSubject.next(resp);

        return undefined;
      })
    );
  }

  /**
   * Check if patient has any regular insurance (excludes EAP, cash pay, usage-based, VA).
   * Returns true if the insurance list is empty.
   */
  isInsuranceListEmpty$(): Observable<boolean> {
    return this.patientInsuranceList$.pipe(
      map((insList) => insList.length === 0)
    );
  }

  /**
   * Use to refresh store and check if we need to send User to mulitpayor screen
   * in insurance page component.
   */
  loadAndCheckMultipayor(): Observable<boolean> {
    return this.clientInsuranceHttpService.listTempImprovement({ view: 'with_provider_network_detail' }).pipe(
      switchMap(
        (resp) => {
          this.fullListSubject.next(resp);
          const insuranceList = resp.filter((i) => this.isRegularInsurance(i));
          return of(insuranceList.length > 1);
        }
      )
    );
  }

  deleteInsurance(ins: ITempImprovementPatientInsurances): Observable<void> {
    return this.clientInsuranceHttpService.delete(ins.id).pipe(
      map(() => {
        const updatedList = this.fullListSubject.getValue().filter((insurance) => insurance.id !== ins.id);
        this.fullListSubject.next(updatedList);
      })
    );
  }

  /**
   * Remove multiple insurance records from client account.
   */
  deleteMultipleInsurances(insurances: ITempImprovementPatientInsurances[]): void {
    const insuranceIds = insurances.map((ins) => ins.id.toString());
    this.clientInsuranceHttpService.deleteMultiple(insuranceIds).subscribe({
      next: () => {
        const updatedList = this.fullListSubject.getValue().filter(
          (insurance) => !insuranceIds.includes(insurance.id.toString())
        );
        this.fullListSubject.next(updatedList);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  isRegularInsurance(ins: ITempImprovementPatientInsurances): boolean {
    return !ins.providerNetworkCompany?.isCashPay
      && !ins.providerNetworkCompany?.eap
      && !ins.providerNetworkCompany?.isUsageBased
      && !ins.providerNetworkCompany?.veteransAffairs;
  }

  showSuggestedPayerModal(): void {
    this.suggestedPayerModalSubject.next(true);
  }

  hideSuggestedPayerModal(): void {
    this.suggestedPayerModalSubject.next(false);
  }

  getBenefitsInfo$(): Observable<IBenefitsInformation> {
    return this.patientInsuranceList$
      .pipe(
        filter((insuranceList) => insuranceList.length > 0),
        first(),
        withLatestFrom(this.benefitsInfo$),
        switchMap(([insuranceList, benefitsInfo]) => {
          const insuranceId = insuranceList?.[0]?.id;
          const benefits = benefitsInfo.get(insuranceId);

          if (benefits) return of(benefits);

          return this.clientInsuranceHttpService
            .fetchLastBenefitInformation$(insuranceId)
            .pipe(
              tap((info) => {
                const allBenefits = this.benefitsInfoSubject.value;
                allBenefits.set(insuranceId, info);
                this.benefitsInfoSubject.next(allBenefits);
              })
            );
        }));
  }
}
